import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaFilter, FaFileExport, FaEye, FaArrowLeft, FaInfoCircle, FaSync } from 'react-icons/fa';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import Swal from 'sweetalert2';
import ReactPaginate from 'react-paginate';
import ExpenseModal from '../project/expense/ExpenseModal';
import withPermission from '../../utils/withPermission';
import { Dropdown } from 'react-bootstrap';

const ExpenseReport = () => {
    const { id } = useParams(); // Project ID (optional)
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState({
        totalAmount: 0,
        totalVAT: 0,
        adjustmentAmount: 0,
        grandTotal: 0,
        totalAmountAED: 0,
        totalVATAED: 0,
        adjustmentAmountAED: 0,
        grandTotalAED: 0,
        totalWPSCharge: 0,
        totalWPSVAT: 0,
        paymentMethods: {
            cash: 0,
            bank: 0,
            other: 0
        },
        paymentMethodsAED: {
            cash: 0,
            bank: 0,
            other: 0
        }
    });

    const [filters, setFilters] = useState({
        fromDate: dayjs().startOf('month').format('YYYY-MM-DD'),
        toDate: dayjs().endOf('month').format('YYYY-MM-DD'),
        project: id || '',
        vendor: '',
        client: '',
        category: '',
        expenseType: '',
        user: '',
        dateRange: 'yearly',
        paymentStatus: 'all',
        source: []
    });

    const [selectedEndYear, setSelectedEndYear] = useState(null);

    const [filterOptions, setFilterOptions] = useState({
        projects: [],
        vendors: [],
        clients: [],
        categories: [],
        users: []
    });

    const [selectedYear, setSelectedYear] = useState(null);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const itemsPerPage = 20;

    const fetchFilterOptions = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/expense-report/filter-options`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            setFilterOptions(response.data);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const fetchReportData = async () => {
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            let params = new URLSearchParams();
            params.append('fromDate', filters.fromDate);
            params.append('toDate', filters.toDate);
            params.append('dateRange', filters.dateRange);
            if (filters.project) params.append('project', filters.project);
            if (filters.vendor) params.append('vendor', filters.vendor);
            if (filters.client) params.append('client', filters.client);
            if (filters.category) params.append('category', filters.category);
            if (filters.user) params.append('user', filters.user);
            if (filters.paymentStatus !== 'all') params.append('paymentStatus', filters.paymentStatus);
            if (filters.source && filters.source.length > 0) {
                params.append('source', filters.source.join(','));
            }

            const response = await axios.get(`${backendUrl}/api/expense-report?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            if (response.data.status === 'success') {
                const allExpenses = response.data.data;
                setExpenses(allExpenses);
                setPageCount(Math.ceil(allExpenses.length / itemsPerPage));
                
                // Map summary data from backend
                const backendSummary = response.data.summary;
                setSummary({
                    totalAmount: backendSummary.total_excluding_vat,
                    totalVAT: backendSummary.total_vat,
                    adjustmentAmount: backendSummary.total_adjustment,
                    grandTotal: backendSummary.total_including_vat,
                    totalAmountAED: backendSummary.total_excluding_vat_aed,
                    totalVATAED: backendSummary.total_vat_aed,
                    adjustmentAmountAED: backendSummary.total_adjustment_aed,
                    grandTotalAED: backendSummary.total_including_vat_aed,
                    totalWPSCharge: backendSummary.total_wps_charge || 0,
                    totalWPSVAT: backendSummary.total_wps_vat || 0,
                    paymentMethods: { cash: 0, bank: 0, other: 0 }, // Optional: recalculate if needed
                    paymentMethodsAED: { cash: 0, bank: 0, other: 0 }
                });

                // Audit capture for parity checks
                const debugData = allExpenses.map(e => ({
                    id: e.expense_id,
                    date: e.date,
                    narration: (e.narration || "No Narration").trim().toLowerCase(),
                    amount: Number(e.total),
                    journal_id: e.journal_id
                }));
                localStorage.setItem('debugExpenseReport', JSON.stringify(debugData));
                console.log(`📊 [ExpenseReport] ${debugData.length} items captured for audit.`);
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            Swal.fire('Error', 'Failed to fetch report data', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
        // Set initial filters from URL if project ID is provided
        if (id) {
            setFilters(prev => ({ ...prev, project: id, dateRange: 'this_year' }));
        }
    }, [id]);

    useEffect(() => {
        fetchReportData();
    }, [filters]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            
            if (name === 'dateRange') {
                setSelectedYear(null);
                setSelectedEndYear(null);
                if (value === 'yearly') {
                    newFilters.fromDate = '';
                    newFilters.toDate = '';
                }
            }
            return newFilters;
        });
    };

    const handleYearChange = (newValue) => {
        setSelectedYear(newValue);
        if (newValue) {
            setFilters(prev => ({
                ...prev,
                fromDate: newValue.startOf('year').format('YYYY-MM-DD'),
                // Auto-sync End Year
                toDate: newValue.endOf('year').format('YYYY-MM-DD')
            }));
            setSelectedEndYear(newValue);
        }
    };

    const handleEndYearChange = (newValue) => {
        setSelectedEndYear(newValue);
        if (newValue) {
            setFilters(prev => ({
                ...prev,
                toDate: newValue.endOf('year').format('YYYY-MM-DD')
            }));
        }
    };

    const handleSourceFilterChange = (source) => {
        setFilters(prev => {
            const newSource = prev.source.includes(source)
                ? prev.source.filter(s => s !== source)
                : [...prev.source, source];
            return { ...prev, source: newSource };
        });
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected);
    };

    const handleExport = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            let params = new URLSearchParams();
            params.append('fromDate', filters.fromDate);
            params.append('toDate', filters.toDate);
            params.append('dateRange', filters.dateRange);
            if (filters.project) params.append('project', filters.project);
            if (filters.vendor) params.append('vendor', filters.vendor);
            if (filters.client) params.append('client', filters.client);
            if (filters.category) params.append('category', filters.category);
            if (filters.user) params.append('user', filters.user);
            if (filters.paymentStatus !== 'all') params.append('paymentStatus', filters.paymentStatus);
            if (filters.source && filters.source.length > 0) {
                params.append('source', filters.source.join(','));
            }

            const response = await axios({
                url: `${backendUrl}/api/expense-report/export?${params.toString()}`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Expense_Report_${filters.fromDate}_to_${filters.toDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export error:', error);
            Swal.fire('Error', 'Failed to export report', 'error');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const displayExpenses = expenses.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center">
                    <button className="btn btn-link p-0 me-3" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <h4 className="mb-0">Expense Report</h4>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleExport}>
                        <FaFileExport /> Export Excel
                    </button>
                    <button className="btn btn-outline-secondary" onClick={fetchReportData}>
                        <FaSync className={loading ? 'fa-spin' : ''} />
                    </button>
                </div>
            </div>

            {/* Filter Section */}
            <div className="card mb-4 border-0 shadow-sm">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-2">
                            <label className="form-label fw-bold small text-muted">Date Range</label>
                            <select 
                                className="form-select form-select-sm" 
                                name="dateRange" 
                                value={filters.dateRange} 
                                onChange={handleFilterChange}
                            >
                                <option value="yearly">Yearly</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {filters.dateRange === 'yearly' && (
                            <>
                                 <div className="col-md-2">
                                     <label className="form-label fw-bold small text-muted">Start Date</label>
                                     <LocalizationProvider dateAdapter={AdapterDayjs}>
                                         <DatePicker
                                             views={["year", "month", "day"]}
                                             openTo="year"
                                             format="DD-MM-YYYY"
                                             value={filters.fromDate ? dayjs(filters.fromDate) : null}
                                             onChange={(newValue) => {
                                                 if (newValue) {
                                                     setSelectedYear(newValue);
                                                     if (!filters.fromDate || dayjs(filters.fromDate).year() !== newValue.year()) {
                                                         const start = newValue.startOf('year');
                                                         const end = newValue.endOf('year');
                                                         setFilters(prev => ({
                                                             ...prev,
                                                             fromDate: start.format('YYYY-MM-DD'),
                                                             toDate: end.format('YYYY-MM-DD')
                                                         }));
                                                         setSelectedEndYear(newValue);
                                                     } else {
                                                         const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, filters.toDate);
                                                         setFilters(prev => ({
                                                             ...prev,
                                                             fromDate: newStart,
                                                             ...(suggestedEndDate && { toDate: suggestedEndDate })
                                                         }));
                                                     }
                                                 }
                                             }}
                                             slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                         />
                                     </LocalizationProvider>
                                 </div>
                                 <div className="col-md-2">
                                     <label className="form-label fw-bold small text-muted">End Date</label>
                                     <LocalizationProvider dateAdapter={AdapterDayjs}>
                                         <DatePicker
                                             views={["year", "month", "day"]}
                                             openTo="year"
                                             format="DD-MM-YYYY"
                                             value={filters.toDate ? dayjs(filters.toDate) : null}
                                             onChange={(newValue) => {
                                                 if (newValue) {
                                                     setSelectedEndYear(newValue);
                                                     if (!filters.toDate || dayjs(filters.toDate).year() !== newValue.year()) {
                                                         setFilters(prev => ({ ...prev, toDate: newValue.endOf('year').format('YYYY-MM-DD') }));
                                                     } else {
                                                         setFilters(prev => ({ ...prev, toDate: getAdjustedEndDate(newValue, filters.fromDate) }));
                                                     }
                                                 }
                                             }}
                                             slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                         />
                                     </LocalizationProvider>
                                 </div>
                            </>
                        )}

                        {filters.dateRange === 'custom' && (
                            <>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold small text-muted">From Date</label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            views={["year", "month", "day"]}
                                            format="DD-MM-YYYY"
                                            value={filters.fromDate ? dayjs(filters.fromDate) : null}
                                            onChange={(newValue) => {
                                                 const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, filters.toDate);
                                                 setFilters(prev => ({
                                                     ...prev,
                                                     fromDate: newStart,
                                                     ...(suggestedEndDate && { toDate: suggestedEndDate })
                                                 }));
                                             }}
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label fw-bold small text-muted">To Date</label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            views={["year", "month", "day"]}
                                            format="DD-MM-YYYY"
                                            value={filters.toDate ? dayjs(filters.toDate) : null}
                                            onChange={(newValue) => {
                                                 setFilters(prev => ({ ...prev, toDate: getAdjustedEndDate(newValue, filters.fromDate) }));
                                             }}
                                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            </>
                        )}

                        <div className="col-md-3">
                            <label className="form-label fw-bold small text-muted">Project</label>
                            <select 
                                className="form-select form-select-sm" 
                                name="project" 
                                value={filters.project} 
                                onChange={handleFilterChange}
                            >
                                <option value="">All Projects</option>
                                {filterOptions.projects.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label className="form-label fw-bold small text-muted">Vendor</label>
                            <select 
                                className="form-select form-select-sm" 
                                name="vendor" 
                                value={filters.vendor} 
                                onChange={handleFilterChange}
                            >
                                <option value="">All Vendors</option>
                                {filterOptions.vendors.map(v => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label className="form-label fw-bold small text-muted">Category</label>
                            <select 
                                className="form-select form-select-sm" 
                                name="category" 
                                value={filters.category} 
                                onChange={handleFilterChange}
                            >
                                <option value="">All Categories</option>
                                {filterOptions.categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-2">
                            <label className="form-label fw-bold small text-muted">Source</label>
                            <Dropdown autoClose="outside">
                                <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100 d-flex justify-content-between align-items-center">
                                    {filters.source.length === 0 ? 'All Sources' : `${filters.source.length} Selected`}
                                </Dropdown.Toggle>
                                <Dropdown.Menu className="w-100 p-2">
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="sourceDirect" checked={filters.source.includes('direct')} onChange={() => handleSourceFilterChange('direct')} />
                                        <label className="form-check-label" htmlFor="sourceDirect">Direct Expense</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="sourcePetty" checked={filters.source.includes('pettycash')} onChange={() => handleSourceFilterChange('pettycash')} />
                                        <label className="form-check-label" htmlFor="sourcePetty">Petty Cash</label>
                                    </div>
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="sourceSalary" checked={filters.source.includes('salary')} onChange={() => handleSourceFilterChange('salary')} />
                                        <label className="form-check-label" htmlFor="sourceSalary">Employee Salary</label>
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(filters.fromDate || filters.toDate || filters.project || filters.vendor || filters.category || (filters.source && filters.source.length > 0)) && (
                        <div className="mt-3">
                            <small className="text-muted d-block mb-1 font-size-11">Active Filters:</small>
                            <div className="d-flex flex-wrap gap-1">
                                {filters.fromDate && filters.toDate && 
                                 dayjs(filters.fromDate).isSame(dayjs(filters.toDate).startOf('year'), 'day') && 
                                 dayjs(filters.toDate).isSame(dayjs(filters.fromDate).endOf('year'), 'day') ? (
                                    <span className="badge bg-info text-white">
                                        Period: {dayjs(filters.fromDate).format('MMM YYYY')} – {dayjs(filters.toDate).format('MMM YYYY')}
                                    </span>
                                ) : (
                                    <>
                                        {filters.fromDate && (
                                            <span className="badge bg-info text-white">
                                                From: {dayjs(filters.fromDate).format('DD-MM-YYYY')}
                                            </span>
                                        )}
                                        {filters.toDate && (
                                            <span className="badge bg-info text-white">
                                                To: {dayjs(filters.toDate).format('DD-MM-YYYY')}
                                            </span>
                                        )}
                                    </>
                                )}
                                {filters.project && (
                                    <span className="badge bg-warning text-dark">
                                        Project: {filterOptions.projects.find(p => p.id == filters.project)?.name}
                                    </span>
                                )}
                                {filters.vendor && (
                                    <span className="badge bg-success text-white">
                                        Vendor: {filterOptions.vendors.find(v => v.id == filters.vendor)?.name}
                                    </span>
                                )}
                                {filters.category && (
                                    <span className="badge bg-primary text-white">
                                        Category: {filterOptions.categories.find(c => c.id == filters.category)?.name}
                                    </span>
                                )}
                                {filters.source && filters.source.length > 0 && (
                                    <span className="badge bg-secondary text-white">
                                        Source: {filters.source.join(', ')}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm bg-primary text-white">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 opacity-75">Total Base Amount</h6>
                            <h3 className="card-title mb-0">{formatCurrency(summary.totalAmount)}</h3>
                            <div className="mt-2 small">AED {formatCurrency(summary.totalAmountAED)}</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm bg-info text-white">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 opacity-75">Total VAT</h6>
                            <h3 className="card-title mb-0">{formatCurrency(summary.totalVAT)}</h3>
                            <div className="mt-2 small">AED {formatCurrency(summary.totalVATAED)}</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm bg-warning text-white">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 opacity-75">Total Adjustment</h6>
                            <h3 className="card-title mb-0">{formatCurrency(summary.adjustmentAmount)}</h3>
                            <div className="mt-2 small">AED {formatCurrency(summary.adjustmentAmountAED)}</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm bg-success text-white">
                        <div className="card-body">
                            <h6 className="card-subtitle mb-2 opacity-75">Grand Total</h6>
                            <h3 className="card-title mb-0">{formatCurrency(summary.grandTotal)}</h3>
                            <div className="mt-2 small font-weight-bold">AED {formatCurrency(summary.grandTotalAED)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Table */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Date</th>
                                    <th>Narration</th>
                                    <th>Project</th>
                                    <th>Vendor</th>
                                    <th>Category</th>
                                    <th className="text-end">Base Amount</th>
                                    <th className="text-end">VAT</th>
                                    <th className="text-end">Adjustment</th>
                                    <th className="text-end pe-4">Total (AED)</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="11" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : displayExpenses.length === 0 ? (
                                    <tr>
                                        <td colSpan="11" className="text-center py-5 text-muted">No expenses found for the selected period.</td>
                                    </tr>
                                ) : (
                                    displayExpenses.map((expense, index) => (
                                        <tr key={index}>
                                            <td className="ps-4 text-nowrap">{dayjs(expense.date).format('DD-MMM-YYYY')}</td>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '200px' }} title={expense.narration}>
                                                    {expense.narration}
                                                </div>
                                                {expense.invoice_no && <small className="text-muted d-block">Inv: {expense.invoice_no}</small>}
                                            </td>
                                            <td>{expense.project || <span className="text-muted">N/A</span>}</td>
                                            <td>{expense.vendor || <span className="text-muted">N/A</span>}</td>
                                            <td><span className="badge bg-light text-dark fw-normal">{expense.category}</span></td>
                                            <td className="text-end">
                                                {formatCurrency(expense.amount_excl_vat)}
                                                <div className="small text-muted">{expense.vendor_currency}</div>
                                            </td>
                                            <td className="text-end">{formatCurrency(expense.vat_amount)}</td>
                                            <td className="text-end">{formatCurrency(expense.adjustment_amount)}</td>
                                            <td className="text-end pe-4 fw-bold">{formatCurrency(expense.total_aed)}</td>
                                            <td>
                                                <span className={`badge rounded-pill ${
                                                    expense.payment_status === 'Paid' ? 'bg-success-subtle text-success' :
                                                    expense.payment_status === 'Partial' ? 'bg-warning-subtle text-warning' :
                                                    'bg-danger-subtle text-danger'
                                                }`}>
                                                    {expense.payment_status}
                                                </span>
                                            </td>
                                            <td>
                                                <button className="btn btn-sm btn-light" onClick={() => {
                                                    setSelectedExpenseId(expense.expense_id);
                                                    setShowExpenseModal(true);
                                                }}>
                                                    <FaEye />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                {pageCount > 1 && (
                    <div className="card-footer bg-white border-0 py-3">
                        <ReactPaginate
                            previousLabel={'Previous'}
                            nextLabel={'Next'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            onPageChange={handlePageClick}
                            containerClassName={'pagination pagination-sm justify-content-center mb-0'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />
                    </div>
                )}
            </div>

            {showExpenseModal && (
                <ExpenseModal
                    show={showExpenseModal}
                    handleClose={() => setShowExpenseModal(false)}
                    expenseId={selectedExpenseId}
                    mode="view"
                />
            )}
        </div>
    );
};

export default ExpenseReport;
